'use client';

import { Box, Container, Paper } from '@mui/material';
import { useTranslations, useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { useAuth } from '@boty/database';
import { AppHeader, UserMenu } from '../../../features/layout';

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const t = useTranslations('creator.auth');
  const locale = useLocale();
  const pathname = usePathname();
  const { user } = useAuth();

  // Hide nav items on quick-signup page (magic link handles both login/signup)
  const isQuickSignup = pathname?.includes('/quick-signup');

  // Show login/signup links only when not authenticated and not on quick-signup
  const navItems = user || isQuickSignup
    ? []
    : [
        { label: t('login'), href: `/${locale}/quick-signup` },
        { label: t('signUp'), href: `/${locale}/quick-signup` },
      ];

  // Show UserMenu when authenticated
  const rightContent = user ? <UserMenu /> : undefined;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <AppHeader navItems={navItems} rightContent={rightContent} />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          py: { xs: 1, sm: 4 },
        }}
      >
        <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 3 } }}>
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              // Remove border/elevation on mobile — page already has a header
              boxShadow: { xs: 'none', sm: 3 },
              backgroundColor: { xs: 'transparent', sm: 'background.paper' },
            }}
          >
            {children}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
